import { useQuery } from '@tanstack/react-query';
import { Select, Tag } from 'antd';
import { type ReactElement, useMemo, useState } from 'react';
import { getTextColor } from '@/std/color';
import { DEBOUNCE_TIME } from '@/std/const/debounceTime';
import { useDebouncedState } from '@/std/hooks/useDebouncedState';
import { attempt } from '@/std/result';
import type { MarcadorLocal } from '../types/enum';
import type { Marcador } from '../types/Marcador';
import { apiMarcadorGetQueryOptions } from '../api/apiMarcadorGet';
import { SelectMarcadorTag } from './SelectMarcadorTag';

type Option = {
    value: number;
    label: string;
    obj: Marcador;
};

const SEM_MARCADOR: Marcador = {
    mar_cor: '',
    mar_empresa_idpk: -1,
    mar_idpk: -1,
    mar_locais: [],
    mar_titulo: 'Sem marcador',
};

export function SelectMarcador({
    local,
    disabled,
    placeholder,
    showSemMarcador = false,
    value,
    onChange,
}: {
    // Indica quais marcadores deverão ser listados
    local?: MarcadorLocal | MarcadorLocal[];

    disabled?: boolean;
    placeholder?: string;

    // se é para exibir o primeiro item "sem marcador"
    showSemMarcador?: boolean;

    // value e newValue seriam do tipo number[]
    // mas devido a limitações do banco,
    // os marcadores são armazenado como string
    value?: string | Marcador[] | null;
    onChange?: (marcadoresStr: string, marcadores: Marcador[]) => void;
}) {
    const [pesquisar, setPesquisar] = useDebouncedState('', DEBOUNCE_TIME);
    const [hasOpened, setHasOpened] = useState(false);

    const innerValue = useMemo(() => {
        if (!value) {
            return [];
        }

        if (Array.isArray(value)) {
            return value.map((m) => m.mar_idpk);
        }

        const resultParse = attempt(() => JSON.parse(value || '[]') as number[]);

        if (resultParse.error) {
            console.error('SelectMarcador: erro ao parsear value', {
                error: resultParse.error,
                value,
            });

            return [];
        }

        return resultParse.value;
    }, [value]);

    const enabled = innerValue.length > 0 || hasOpened;

    const { data, isFetching } = useQuery({
        ...apiMarcadorGetQueryOptions({
            local,
            orderby: 'mar_titulo',
            pesquisar: pesquisar.trim() || undefined,
        }),
        enabled,
    });

    const options = useMemo(() => {
        if (!data) {
            return [];
        }

        const options: Option[] = [];

        if (showSemMarcador) {
            options.push({
                label: 'Sem marcador',
                obj: SEM_MARCADOR,
                value: -1,
            });
        }

        for (const marcador of data.registros) {
            options.push({
                label: marcador.mar_titulo || '',
                obj: marcador,
                value: marcador.mar_idpk,
            });
        }

        return options;
    }, [data, showSemMarcador]);

    const handleChange = (newValue: number[], options: Option[]): void => {
        if (!onChange) {
            return;
        }

        if (newValue.length === 0) {
            onChange('[]', []);
            return;
        }

        const converted = `[${newValue.join(',')}]`;

        onChange(
            converted,
            newValue
                .map(
                    (idpk) =>
                        options.find((option) => option?.value === idpk)?.obj ||
                        (Array.isArray(value)
                            ? value.find((marcador) => marcador.mar_idpk === idpk)
                            : undefined),
                )
                .filter((marcador): marcador is Marcador => marcador !== undefined),
        );
    };

    const defaultPopupRender = (menu: ReactElement) => {
        if (isFetching) {
            return <div>Pesquisando...</div>;
        }

        return menu;
    };

    return (
        <Select
            allowClear={true}
            className='w-full'
            disabled={disabled}
            loading={isFetching}
            mode='multiple'
            onChange={(value, options) => handleChange(value, options as Option[])}
            onOpenChange={(open) => {
                if (open) {
                    setHasOpened(true);
                }
            }}
            optionRender={(option) => {
                const marcador = option.data.obj;

                return (
                    <Tag
                        color={marcador.mar_cor || 'gray'}
                        disabled={false}
                        key={marcador.mar_idpk}
                        style={{ color: getTextColor(marcador.mar_cor || '') }}
                        variant='solid'
                    >
                        {marcador.mar_titulo}
                    </Tag>
                );
            }}
            options={options}
            placeholder={placeholder}
            popupRender={defaultPopupRender}
            showSearch={{ filterOption: false, onSearch: setPesquisar }}
            tagRender={(tagRenderProps) => (
                <SelectMarcadorTag
                    {...tagRenderProps}
                    marcadores={[
                        ...(data?.registros || []),
                        ...(Array.isArray(value) ? value : []),
                    ]}
                />
            )}
            value={innerValue}
        />
    );
}
